import type { SupportedChainName } from "@src/components/DefuseSDK/types/base"
import { validateAddress } from "@src/components/DefuseSDK/utils/validateAddress"

/**
 * Detects the most-likely network for a given address by pattern-matching
 * against the existing per-network validators (`validateAddress.ts`).
 *
 * Pure: no I/O, no on-chain RPC. Caller is responsible for checking the
 * result against feature-flagged network availability before writing to
 * any form state.
 *
 * Returns `null` for ambiguous shapes (e.g., `0x` + 64 hex which is both
 * Sui and Aptos; legacy `3…` Base58 P2SH which is both Bitcoin and Litecoin).
 *
 * Returns `SupportedChainName` (not the wider `ContactNetwork`) so the
 * caller can pass the result straight into `assetNetworkAdapter` without
 * narrowing — `near_intents` is a virtual destination with no detectable
 * address shape and is never returned.
 */
export function detectNetworkFromAddress(
  address: string
): SupportedChainName | null {
  const trimmed = address.trim()
  if (trimmed.length === 0) return null

  // ---- Ambiguous shapes: bail before any single-network match ----

  // 0x + 64 hex → could be Sui or Aptos.
  if (/^0x[a-fA-F0-9]{64}$/.test(trimmed)) return null

  // Bare 64 hex → NEAR implicit, but identical to a "0x"-stripped sui/aptos.
  if (/^[a-fA-F0-9]{64}$/.test(trimmed)) return null

  // Legacy P2SH "3…" → Bitcoin and Litecoin both validate it; codebase
  // explicitly notes this is indistinguishable by prefix.
  if (/^3[1-9A-HJ-NP-Za-km-z]{25,34}$/.test(trimmed)) return null

  // ---- Unambiguous prefix-based detection ----

  // EVM 0x + 40 hex (also valid for NEAR eth-implicit, but EVM is the
  // overwhelmingly common case — spec defaults to eth).
  if (validateAddress(trimmed, "eth")) return "eth"

  // NEAR named accounts only — must contain a `.` (`alice.near`,
  // `alice.tg`, `my.testnet`). NEAR implicit (bare 64-hex) was already
  // filtered above as ambiguous, and the underlying `validateAddress(_, "near")`
  // is too permissive for prefix-less tokens (it accepts any lowercase
  // alphanumeric+`-_` string), which would steal matches from bech32-style
  // addresses like `bc1…` and `qpm2…`. Restricting to dotted names keeps
  // detection unambiguous.
  if (
    /^[a-z\d]+([-_][a-z\d]+)*(\.[a-z\d]+([-_][a-z\d]+)*)+$/.test(trimmed) &&
    validateAddress(trimmed, "near")
  ) {
    return "near"
  }

  // ---- Prefix-based chains ----
  // Each chain whose addresses have a distinguishing prefix gets a chance
  // to claim the address before the Solana fallback runs. Order matters
  // only insofar as more-specific prefixes go first (e.g. `tex1` before any
  // `t…` Solana fallback). Within each block, the prefix gate avoids
  // running an unrelated validator on a clearly-different shape, but each
  // validator's own checksum/format check is the actual decision.

  // XRP — covers both `r…` classic and `X…` X-addresses.
  if (/^[rX]/.test(trimmed) && validateAddress(trimmed, "xrpledger")) {
    return "xrpledger"
  }

  // Tron — `T` + 33 base58.
  if (trimmed.startsWith("T") && validateAddress(trimmed, "tron")) {
    return "tron"
  }

  if (trimmed.startsWith("G") && validateAddress(trimmed, "stellar")) {
    return "stellar"
  }

  if (/^(t1|t3|tex1|u1)/.test(trimmed) && validateAddress(trimmed, "zcash")) {
    return "zcash"
  }

  if (trimmed.startsWith("addr1") && validateAddress(trimmed, "cardano")) {
    return "cardano"
  }

  if (
    (trimmed.startsWith("bc1") || trimmed.startsWith("1")) &&
    validateAddress(trimmed, "bitcoin")
  ) {
    return "bitcoin"
  }

  if (/^[DA]/.test(trimmed) && validateAddress(trimmed, "dogecoin")) {
    return "dogecoin"
  }

  if (
    (trimmed.startsWith("ltc1") ||
      trimmed.startsWith("L") ||
      trimmed.startsWith("M")) &&
    validateAddress(trimmed, "litecoin")
  ) {
    return "litecoin"
  }

  if (
    (trimmed.startsWith("bitcoincash:") || trimmed.startsWith("q")) &&
    validateAddress(trimmed, "bitcoincash")
  ) {
    return "bitcoincash"
  }

  if (/^(EQ|UQ)/.test(trimmed) && validateAddress(trimmed, "ton")) {
    return "ton"
  }

  // ---- Solana fallback ----
  // Solana base58 pubkeys decode to 32 bytes — 32–44 chars in base58
  // (typically 43–44 in practice). Gate on shape + length before running
  // `PublicKey.isOnCurve` so random base58 of the wrong length doesn't
  // reach the curve check at all. Prefix-based chains have already had
  // their turn above; anything that gets here should be a real Solana
  // pubkey or noise we want to reject. The earlier prefix-exclusion
  // approach was wrong (it rejected valid Solana keys starting with `G`,
  // `T`, etc.), so we still run Solana last and unconditionally relative
  // to other chains — just with a length gate to keep the surface tight.
  if (
    /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed) &&
    validateAddress(trimmed, "solana")
  ) {
    return "solana"
  }

  return null
}
